import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Send, Radar, Loader2, Cpu, Lock, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PRICING_ENABLED } from "../config";

const SESSION = "main";
const SUGGESTIONS = [
  "What's the difference between Mode 1 and Mode 2?",
  "How do I pass the FAA Part 107 exam?",
  "Why is my drone drifting in a hover?",
  "What weather is too windy to fly?",
];

export default function Instructor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadingHist, setLoadingHist] = useState(true);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("gpt-4o");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/ai/models").then((r) => setModels(r.data)).catch(() => {});
    api.get(`/ai/chat/history?session_id=${SESSION}`)
      .then((r) => setMessages(r.data.map((m) => ({ role: m.role, content: m.content }))))
      .finally(() => setLoadingHist(false));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("da_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: SESSION, model }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (user && !user.has_paid) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 pt-16">
          <div className="p-8 rounded-sm border border-accent/40 bg-card text-center" data-testid="instructor-paywall">
            <div className="h-16 w-16 mx-auto rounded-sm bg-accent/15 flex items-center justify-center mb-5">
              <Lock className="h-8 w-8 text-accent" />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">Meet Skyler — Premium</h1>
            <p className="text-slate-400 max-w-md mx-auto mb-7">
              Your personal AI flight instructor is part of the full DronAcademy course — unlimited coaching, all modules and Part 107 prep. Full course access is coming soon.
            </p>
            {PRICING_ENABLED && (
              <Link to="/checkout">
                <button className="h-11 px-7 rounded-sm bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors" data-testid="instructor-unlock-btn">
                  Unlock Full Access
                </button>
              </Link>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 pt-6 flex flex-col">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-sm bg-primary/15 flex items-center justify-center glow-cyan">
              <Radar className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold leading-none">Skyler</h1>
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">AI Flight Instructor · Online</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2" data-testid="model-picker">
              <Cpu className="h-4 w-4 text-muted-foreground" />
              <Select value={model} onValueChange={setModel} disabled={streaming}>
                <SelectTrigger className="w-[190px] h-9 bg-secondary/40 border-border text-sm" data-testid="model-select-trigger">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {models.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.id}`}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="instructor-close-btn"
              className="h-9 px-4 rounded-sm border-border/70 bg-secondary/40 hover:bg-secondary text-sm gap-1.5">
              <X className="h-4 w-4" /> Close
            </Button>
          </div>
        </div>

        <div className="flex-1 rounded-sm border border-border/70 bg-card scanlines overflow-hidden flex flex-col min-h-[55vh]">
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-log">
            {loadingHist ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <Radar className="h-10 w-10 text-primary/50 mb-4" />
                <p className="text-slate-400 max-w-sm mb-6">Ask me anything about flying drones, safety, maneuvers or the FAA Part 107 exam.</p>
                <div className="grid sm:grid-cols-2 gap-2 w-full max-w-lg">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`suggestion-${i}`}
                      className="text-left text-sm px-4 py-3 rounded-sm border border-border/70 bg-secondary/30 hover:border-primary/50 hover:text-primary transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`msg-${m.role}-${i}`}
                    className={`max-w-[85%] px-4 py-3 rounded-sm text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary/60 border border-border/60 font-mono text-slate-200"
                    }`}>
                    {m.content || (streaming && i === messages.length - 1 ? <span className="cursor-blink">▋</span> : "")}
                  </div>
                </motion.div>
              ))
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border/60 p-3 flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Skyler…"
              data-testid="chat-input" disabled={streaming}
              className="bg-secondary/40 border-border h-11" />
            <Button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-11 w-11 p-0 shrink-0">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">Skyler is AI-generated and can make mistakes. Always verify current regulations.</p>
      </main>
    </div>
  );
}
